import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Dropdown as DropdownNs } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as ReactLink, LinkProps } from 'react-router-dom';

import { Button } from '../Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '../Dropdown';
import { InfoOutline, MoreVerticallySmall } from '../Icons';
import { Link } from '../Link';
import { Skeleton } from '../Skeleton';
import { Tooltip } from '../Tooltip';

const toLines = (line: string) => {
  if (isEmpty(line.trim())) {
    return null;
  }

  return <div key={line}>{line}</div>;
};

interface WrapperProps {
  className?: string;
  dataTestId?: string;
  markerColor?: string;
  variant?: 'white' | 'gray';
}

const Wrapper = ({ className, children, markerColor, variant = 'white' }: React.WithChildren<WrapperProps>) => (
  <div
    className={cn(
      'relative flex flex-col gap-y-2 overflow-hidden rounded-regular p-4',
      {
        'bg-neutral-98': variant === 'gray',
        'bg-white': variant === 'white',
      },
      className,
    )}
  >
    {isDefined(markerColor) && (
      <div className="absolute left-0 right-0 top-0 h-0.5 bg-neutral-20" style={{ backgroundColor: markerColor }} />
    )}
    {children}
  </div>
);

interface HeaderProps {
  isLoading?: boolean;
}

const Header = ({ children, isLoading = false }: React.WithOptionalChildren<HeaderProps>) => {
  if (isLoading) {
    return <Skeleton type="text" size="xsmall" width={25} itemClassName="my-0.5" />;
  }

  return <div className="flex items-center gap-2 text-neutral-40 body-5">{children}</div>;
};

interface TitleProps {
  tooltip?: React.ReactNode;
  isLoading?: boolean;
}

const Title = ({ children, tooltip, isLoading = false }: React.WithOptionalChildren<TitleProps>) => {
  if (isLoading) {
    return <Skeleton type="text" width={75} size="xlarge" />;
  }

  return (
    <div className="flex items-center gap-2">
      <h3 className="overflow-hidden break-words heading-3">{children}</h3>
      {isDefined(tooltip) && (
        <Tooltip content={tooltip}>
          <button type="button" className="relative z-elevated after:absolute after:-inset-2">
            <InfoOutline className="h-5 w-5" />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

interface CardLinkProps extends LinkProps {
  isLoading?: boolean;
}

const CardLink = ({ children, isLoading, ...props }: CardLinkProps) => {
  if (isLoading) {
    return <Skeleton type="text" width={75} itemClassName="my-0.5" />;
  }

  return (
    <Link size="large" className="w-fit" {...props}>
      {children}
    </Link>
  );
};

interface MenuProps {
  title: string;
  items: DropdownNs.MenuItem[];
  isDisabled?: boolean;
  className?: string;
  onClick?: VoidFunction;
}

const Menu = ({ title, items, isDisabled, className, onClick }: MenuProps) => {
  const { t } = useTranslation(['common']);

  return (
    <Dropdown title={title} placement="bottom-end">
      <DropdownTrigger>
        <Button
          aria-label={t('common:option_other')}
          variant="text"
          color="secondary"
          className={cn('absolute right-4 top-4 overflow-visible after:absolute after:-inset-2', className)}
          disabled={isDisabled}
          icon={MoreVerticallySmall}
          onClick={onClick}
        />
      </DropdownTrigger>
      <DropdownMenu items={items} />
    </Dropdown>
  );
};

interface AttributeListProps {
  className?: string;
  isLoading?: boolean;
  skeletonRows?: number;
  orientation?: 'vertical' | 'horizontal';
}

const AttributeList = ({
  children,
  className,
  isLoading,
  skeletonRows,
  orientation = 'vertical',
}: React.WithOptionalChildren<AttributeListProps>) => {
  if (isLoading) {
    return (
      <Skeleton
        type="text"
        size="xsmall"
        containerClassName={cx('gap-1.5', { 'flex-row justify-between': orientation === 'horizontal' })}
        rows={skeletonRows}
        width={30}
        unevenWidth={40}
        lastWidth={orientation === 'vertical' ? 50 : 30}
      />
    );
  }

  return (
    <dl
      className={cn('flex flex-wrap justify-between gap-x-1 gap-y-0.5 empty:hidden', { 'flex-col': orientation === 'vertical' }, className)}
    >
      {children}
    </dl>
  );
};

interface AttributeProps {
  className?: string;
  name: string | JSX.Element;
  value: React.ReactNode | React.ReactNode[];
  dataTestId?: string;
  orientation?: 'vertical' | 'horizontal';
}

const Attribute = ({ name, value, className, dataTestId, orientation = 'horizontal' }: AttributeProps) => (
  <div data-testid={dataTestId} className={cn('max-w-full break-words body-5', { 'flex flex-col': orientation === 'vertical' }, className)}>
    <dt className="contents">
      <span className="whitespace-pre text-neutral-40">
        {name}:{'  '}
      </span>
    </dt>
    <dd className="contents" data-sentry-mask>
      <span data-sentry-mask className={cn('text-neutral-20', { 'body-4': orientation === 'vertical' })}>
        {Array.isArray(value) ? value.map(toLines) || '-' : value || '-'}
      </span>
    </dd>
  </div>
);

interface AttributeLinkProps extends Omit<AttributeProps, 'value'> {
  to: LinkProps['to'];
  target?: LinkProps['target'];
  value?: string;
}

const AttributeLink = ({ name, value = '', className, dataTestId, orientation = 'horizontal', to, target }: AttributeLinkProps) => (
  <div data-testid={dataTestId} className={cn('w-full break-words body-5', { 'flex flex-col': orientation === 'vertical' }, className)}>
    <dt className="contents">
      <span className="whitespace-pre text-neutral-40">
        {name}:{'  '}
      </span>
    </dt>
    <dd className="contents" data-sentry-mask>
      <span className={cn('text-neutral-20', { 'body-4': orientation === 'vertical' })}>
        {isEmpty(value) ?
          '-'
        : <ReactLink
            data-sentry-mask
            to={to}
            target={target}
            className="relative z-elevated underline underline-offset-2 after:absolute after:-inset-[7px] focus-visible:focus-outline"
        >
            {value}
          </ReactLink>
        }
      </span>
    </dd>
  </div>
);

interface SummaryListProps {
  isLoading?: boolean;
  skeletonRows?: number;
}

const SummaryList = ({ children, isLoading, skeletonRows = 1 }: React.WithOptionalChildren<SummaryListProps>) => {
  if (isLoading) {
    return (
      <div className="flex justify-between gap-2">
        {Array.from({ length: skeletonRows }, (_, index) => (
          <div key={index} className="mt-1 flex w-full flex-col gap-1 last:items-end">
            <Skeleton type="text" size="xsmall" width={20} />
            <Skeleton type="text" size="small" width={50} />
          </div>
        ))}
      </div>
    );
  }

  return <dl className="flex justify-between gap-2 empty:hidden">{children}</dl>;
};

interface SummaryProps {
  name: string;
  value: React.ReactNode;
}

const Summary = ({ name, value }: SummaryProps) => (
  <div className="flex flex-col text-right first:text-left">
    <dt className="text-neutral-40 body-5">{name}:</dt>
    <dd data-sentry-mask className="text-neutral-20 body-1">
      {value}
    </dd>
  </div>
);

interface ExpanderProps {
  children: React.ReactNode;
  expanderLabel: string;
  isExpanded: boolean;
  toggleExpanded: () => void;
  isLoading?: boolean;
}

const Expander = ({ children, expanderLabel, isExpanded, toggleExpanded, isLoading }: ExpanderProps) => {
  const buttonId = React.useId();
  const sectionId = React.useId();
  const inertProp = isExpanded ? undefined : { inert: '' };

  if (isLoading) {
    return <Skeleton type="rectangle" aspectRatio={1} className="absolute right-4 top-4 w-5 rounded-full" />;
  }

  return (
    <>
      <Button
        id={buttonId}
        aria-controls={sectionId}
        aria-expanded={isExpanded}
        aria-label={expanderLabel}
        variant="text"
        color="secondary"
        className="absolute right-4 top-4 z-elevated overflow-visible after:absolute after:-inset-3"
        isChevronOpen={isExpanded}
        onClick={toggleExpanded}
      />
      <section
        {...inertProp}
        id={sectionId}
        aria-labelledby={buttonId}
        className={cn('grid grid-rows-0 motion-safe:transition-grid', { 'grid-rows-strict-1': isExpanded })}
      >
        <div className="-mx-1 overflow-hidden px-1">{children}</div>
      </section>
    </>
  );
};

export const Card = {
  Attribute,
  AttributeLink,
  AttributeList,
  Expander,
  Header,
  Link: CardLink,
  Menu,
  Summary,
  SummaryList,
  Title,
  Wrapper,
};
